import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client'; 
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"; 
import { Loader2, AlertCircle, PlusCircle } from 'lucide-react';
import { formatPrice } from '@/lib/utils'; 
import { Button } from '@/components/ui/button';
import CreateCourseDialog from './CreateCourseDialog';
import EditCourseDialog from './EditCourseDialog';
import type { StripeProduct, StripePrice } from '@/types/index'; 

// Remove the import for @tanstack/react-table and define our own simplified types
type CellContext<TData, TValue> = {
  getValue: () => TValue;
  row: { original: TData };
  renderValue: () => React.ReactNode;
};

type ColumnDef<TData, TValue = unknown> = {
  accessorKey?: string;
  id?: string;
  header?: string | React.ReactNode;
  cell?: (info: TValue | TData) => React.ReactNode;
};

const fetchStripeProducts = async () => {
  const { data, error } = await supabase.functions.invoke('get-stripe-products');

  if (error) {
    console.error('Error invoking get-stripe-products function:', error);
    throw new Error(error.message);
  }

  if (!data || !Array.isArray(data.products)) {
     throw new Error('Invalid data structure returned from function.');
  }

  return data.products as StripeProduct[];
};

const AdminCoursesPage: React.FC = () => {
  const { data: products, isLoading, isError, error } = useQuery<StripeProduct[], Error>({
    queryKey: ['admin', 'stripeProducts'],
    queryFn: fetchStripeProducts,
  });

  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<StripeProduct | null>(null);

  const handleEditClick = (product: StripeProduct) => {
    setEditingProduct(product);
    setIsEditDialogOpen(true);
  };

  const columns: Array<ColumnDef<StripeProduct>> = [
    { accessorKey: 'id', header: 'Stripe Product ID' },
    { accessorKey: 'name', header: 'Name' },
    { accessorKey: 'description', header: 'Description' },
    {
      accessorKey: 'default_price',
      header: 'Default Price',
      cell: (info: StripePrice | null) => { 
        if (!info || typeof info !== 'object') {
          return <span>N/A</span>;
        }

        const priceDetails = info;
 
        if (priceDetails.unit_amount === null || priceDetails.unit_amount === undefined) {
          return <span>Not Set</span>;
        }
        
        return formatPrice(priceDetails.unit_amount, priceDetails.currency);
      },
    },
    { accessorKey: 'created', header: 'Created (Stripe)', cell: (timestamp: number) => new Date(timestamp * 1000).toLocaleDateString() },
    { 
        id: 'actions',
        header: 'Actions',
        cell: (product: StripeProduct) => ( 
            <Button 
                variant="outline" 
                size="sm"
                onClick={() => handleEditClick(product)}
            >
                Edit
            </Button>
        )
    }
  ];

  return (
    <div className="container mx-auto py-8">
      <div className="flex justify-between items-center mb-6">
         <h1 className="text-3xl font-bold">Admin - Manage Courses</h1>
         <Button onClick={() => setIsCreateDialogOpen(true)}> 
           <PlusCircle className="mr-2 h-4 w-4" /> Create New Course
         </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center items-center py-10">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      )}

      {isError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Fetching Courses</AlertTitle>
          <AlertDescription>
            {error?.message || 'An unknown error occurred.'}
          </AlertDescription>
        </Alert>
      )}

      {!isLoading && !isError && products && (
        <Table>
          <TableCaption>A list of your Stripe products (courses).</TableCaption>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead key={col.accessorKey || col.id}>{col.header}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                {columns.map((col) => {
                  const rawValue = col.accessorKey ? product[col.accessorKey as keyof StripeProduct] : null;
                  
                  let cellContent: React.ReactNode = null; 
                  if (col.cell) {
                    if (col.id === 'actions') {
                      cellContent = col.cell(product); 
                    } else if (col.accessorKey) {
                      cellContent = col.cell(rawValue); 
                    } else {
                      cellContent = 'N/A'; 
                    }
                  } else {
                     if (typeof rawValue === 'string' || typeof rawValue === 'number' || typeof rawValue === 'boolean') {
                        cellContent = String(rawValue); 
                      } else if (rawValue === null || rawValue === undefined) {
                          cellContent = '-'; 
                      } else {
                          cellContent = JSON.stringify(rawValue); 
                      }
                  }
                  
                  return (
                    <TableCell key={col.accessorKey || col.id}>
                      {cellContent}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <CreateCourseDialog
        isOpen={isCreateDialogOpen}
        onClose={() => setIsCreateDialogOpen(false)}
      />
      {editingProduct && (
        <EditCourseDialog 
          isOpen={isEditDialogOpen} 
          onClose={() => {
            setIsEditDialogOpen(false);
            setEditingProduct(null); 
          }}
          editingProduct={editingProduct}
        />
      )}
    </div>
  );
};

export default AdminCoursesPage;
